import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { BlogCategory } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteBlogCategory } from '../hooks/use-blog-category-mutations';

export interface DeleteBlogCategoryDialogProps {
  storeId: string;
  row: BlogCategory | null;
  onOpenChange: (open: boolean) => void;
}

/** Posts keep their rows and simply lose the category. */
export function DeleteBlogCategoryDialog({
  storeId,
  row,
  onOpenChange,
}: DeleteBlogCategoryDialogProps) {
  const { t } = useTranslation(['blog', 'common', 'errors']);
  const remove = useDeleteBlogCategory(storeId);
  const message = useErrorMessage();
  const requestId = isApiError(remove.error) ? remove.error.requestId : undefined;

  return (
    <AlertDialog
      open={row !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('blog:deleteCategory.title', { title: row?.title ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('blog:deleteCategory.description', { count: row?.postCount ?? 0 })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {message(remove.error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (row) remove.mutate(row.id, { onSuccess: () => onOpenChange(false) });
            }}
          >
            {remove.isPending
              ? t('blog:deleteCategory.submitting')
              : t('blog:deleteCategory.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
